<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { Play, Pause, RotateCcw, RotateCw, X, Loader2 } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import { userSettings } from '../stores/settings';
  import { resolveApiUrl } from '../lib/utils';
  import { getTTSFromCache, saveTTSToCache } from '../lib/tts-cache';

  interface Props {
    text: string;
    onClose: () => void;
  }

  let { text, onClose }: Props = $props();

  let isLoading = $state(false);
  let isPlaying = $state(false);
  let error = $state<string | null>(null);
  let progress = $state(0);
  let duration = $state(0);
  let currentTime = $state(0);
  
  // Use regular variable for audio to avoid reactivity issues with cleanup
  let audioElement: HTMLAudioElement | null = null;
  let audioUrl: string | null = null;
  let isClosing = false;
  let hasAudio = $state(false);

  async function loadAndPlay() {
    if (isLoading) return;
    
    isLoading = true;
    error = null;

    try {
      const voiceId = $userSettings?.elevenlabs_voice || 'brian';
      
      // Check cache first
      const cachedBlob = await getTTSFromCache(text, voiceId);
      
      let audioBlob: Blob;
      
      if (cachedBlob) {
        console.log('🎵 Using cached TTS audio');
        audioBlob = cachedBlob;
      } else {
        console.log('🔊 Fetching TTS audio...', { textLength: text.length, voiceId });
        
        const response = await fetch(resolveApiUrl('/api/tts'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text,
            voiceId,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = 'TTS generation failed';
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          } catch {
            // Not JSON, use text
          }
          console.error('TTS API error:', response.status, errorText);
          throw new Error(`${errorMessage} (${response.status})`);
        }

        audioBlob = await response.blob();
        
        console.log('✅ TTS audio received:', { 
          size: audioBlob.size, 
          type: audioBlob.type 
        });

        if (audioBlob.size === 0) {
          throw new Error('Empty audio data received');
        }
        
        // Save to cache for future use
        await saveTTSToCache(text, voiceId, audioBlob);
      }
      
      // Cleanup previous audio URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      audioUrl = URL.createObjectURL(audioBlob);
      
      console.log('🎵 Audio URL created:', audioUrl);
      
      // Create and setup audio element
      audioElement = new Audio(audioUrl);
      
      audioElement.addEventListener('loadedmetadata', () => {
        console.log('Audio metadata loaded:', { duration: audioElement?.duration });
        if (audioElement) {
          duration = audioElement.duration;
        }
      });
      
      audioElement.addEventListener('timeupdate', () => {
        if (audioElement) {
          currentTime = audioElement.currentTime;
          progress = (audioElement.currentTime / audioElement.duration) * 100;
        }
      });
      
      audioElement.addEventListener('ended', () => {
        isPlaying = false;
        progress = 0;
        currentTime = 0;
      });
      
      audioElement.addEventListener('error', (e) => {
        // Ignore errors when closing (caused by cleanup)
        if (isClosing) return;
        
        const audioError = audioElement?.error;
        console.error('Audio playback error:', {
          event: e,
          code: audioError?.code,
          message: audioError?.message
        });
        if (audioError?.message) {
          error = audioError.message;
        }
        isPlaying = false;
      });

      audioElement.addEventListener('canplaythrough', () => {
        console.log('Audio can play through');
      });

      // Start playback
      await audioElement.play();
      isPlaying = true;
      hasAudio = true;

    } catch (err) {
      console.error('TTS error:', err);
      error = err instanceof Error ? err.message : 'TTS generation failed';
    } finally {
      isLoading = false;
    }
  }

  function togglePlayPause() {
    if (!audioElement) {
      loadAndPlay();
      return;
    }
    
    if (isPlaying) {
      audioElement.pause();
      isPlaying = false;
    } else {
      audioElement.play();
      isPlaying = true;
    }
  }

  function skipBackward() {
    if (audioElement) {
      audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
    }
  }

  function skipForward() {
    if (audioElement) {
      audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 10);
    }
  }

  function handleProgressClick(e: MouseEvent) {
    if (!audioElement || !duration) return;
    
    const target = e.currentTarget as HTMLDivElement;
    const rect = target.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    audioElement.currentTime = clickPosition * duration;
  }

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function cleanupAudio() {
    isClosing = true;
    hasAudio = false;
    if (audioElement) {
      audioElement.pause();
      audioElement.src = '';
      audioElement = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      audioUrl = null;
    }
  }

  function handleClose() {
    cleanupAudio();
    onClose();
  }

  // Start loading when component mounts
  onMount(() => {
    loadAndPlay();
  });
  
  // Cleanup when component is destroyed
  onDestroy(() => {
    cleanupAudio();
  });
</script>

<div class="bg-card border border-border rounded-2xl p-4 shadow-lg">
  <!-- Close button -->
  <div class="flex justify-end mb-2">
    <button
      type="button"
      onclick={handleClose}
      class="btn btn-ghost btn-sm h-8 w-8 p-0"
      aria-label={$_('buttons.close')}
    >
      <X class="h-4 w-4" />
    </button>
  </div>

  {#if error}
    <div class="text-center py-4">
      <p class="text-destructive text-sm">{error}</p>
      <button
        type="button"
        onclick={loadAndPlay}
        class="btn btn-primary btn-sm mt-2"
      >
        {$_('buttons.retry') || 'Zkusit znovu'}
      </button>
    </div>
  {:else}
    <!-- Progress bar -->
    <div 
      class="w-full h-2 bg-muted rounded-full cursor-pointer mb-4"
      onclick={handleProgressClick}
      onkeydown={(e) => e.key === 'Enter' && handleProgressClick(e as unknown as MouseEvent)}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress}
      tabindex="0"
    >
      <div 
        class="h-full bg-primary rounded-full transition-all duration-100"
        style="width: {progress}%"
      ></div>
    </div>

    <!-- Time display -->
    <div class="flex justify-between text-xs text-muted-foreground mb-4">
      <span>{formatTime(currentTime)}</span>
      <span>{formatTime(duration)}</span>
    </div>

    <!-- Controls -->
    <div class="flex items-center justify-center gap-6">
      <!-- Skip backward 10s -->
      <button
        type="button"
        onclick={skipBackward}
        disabled={isLoading || !hasAudio}
        class="btn btn-ghost h-12 w-12 p-0 disabled:opacity-50"
        aria-label={$_('tts.skip_backward')}
      >
        <div class="relative">
          <RotateCcw class="h-6 w-6" />
          <span class="absolute -top-1 -right-1 text-[10px] font-bold">10</span>
        </div>
      </button>

      <!-- Play/Pause -->
      <button
        type="button"
        onclick={togglePlayPause}
        disabled={isLoading}
        class="btn btn-primary h-14 w-14 p-0 rounded-full flex items-center justify-center disabled:opacity-50"
        aria-label={isPlaying ? $_('tts.pause') : $_('tts.play')}
      >
        {#if isLoading}
          <Loader2 class="h-7 w-7 animate-spin" />
        {:else if isPlaying}
          <Pause class="h-7 w-7" />
        {:else}
          <Play class="h-7 w-7 ml-1" />
        {/if}
      </button>

      <!-- Skip forward 10s -->
      <button
        type="button"
        onclick={skipForward}
        disabled={isLoading || !hasAudio}
        class="btn btn-ghost h-12 w-12 p-0 disabled:opacity-50"
        aria-label={$_('tts.skip_forward')}
      >
        <div class="relative">
          <RotateCw class="h-6 w-6" />
          <span class="absolute -top-1 -right-1 text-[10px] font-bold">10</span>
        </div>
      </button>
    </div>

    {#if isLoading}
      <p class="text-center text-sm text-muted-foreground mt-4">
        {$_('tts.generating')}
      </p>
    {/if}
  {/if}
</div>
